import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../core/redux/api/reduxStandardApiAction';
import * as R from 'ramda';
import { IAppState } from '../../core/redux/IAppState';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../core/redux/api/circuitBreaker/module';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { apiGuestComments } from '../../core/redux/api/endpoints';
import { GuestComment } from './guestComment';
import { assoc } from '../../language/module';
import { getResource } from '../../language/resources/getResource';


const KEY_ACTION = ACTION_TYPE.GUEST_COMMENTS_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.GUEST_COMMENTS_REQUEST,
  ACTION_TYPE.GUEST_COMMENTS_RESPONSE,
  ACTION_TYPE.GUEST_COMMENTS_ERROR,
];


/**
 * Makes an action that can be dispatched to get a specific guest's history of the comments guest agents have left on
 * them.
 */
export const getGuestCommentsAction = (guestId: string) => makeApiAction(
  {
    method:   'GET',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: apiGuestComments(guestId),
    types:    ACTIONS.map(action => ({ type: action, meta: { guestId } })) as RsaaTypes,
    bailout:  (state: IAppState) => !!getLoading(state, guestId, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<GuestComment[]> = (state, action, error, isLoading, payload) => {
  const guestId = action.meta.guestId;

  if (isLoading) {
    return addLoading(state, guestId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, guestId, KEY_ACTION, error || getResource(state.locale, 'COMMENT_HISTORY_FETCH_ERROR'));
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, guestId, KEY_ACTION);

  // Manually add the guest ID to the results since the API doesn't give them to us as such
  const comments = (payload || []).map(item => assoc('guestId', guestId, item));

  return assoc(
    'commentHistories',
    R.concat(
      comments,
      noLoadingOrError.commentHistories.filter(comment => comment.guestId !== guestId),
    ),
    noLoadingOrError,
  );
};


export const getGuestCommentsReducer = makeApiReducer<GuestComment[]>(ACTIONS, KEY_ACTION, reducer);
